import { useCallback, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Response } from 'request-dot-js'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { useGoogleDrivePicker, PickerSelection } from 'hooks/useGoogleDrivePicker'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { GoogleDriveClient, GoogleDriveSyncState } from 'api/clients/GoogleDriveClient'

dayjs.extend(relativeTime)

interface UseGoogleDriveIntegrationReturn {
  integration: any | undefined
  isConnected: boolean
  filesCount: number
  lastSync: string | null
  syncState: GoogleDriveSyncState | undefined
  isSyncing: boolean
  isPickerLoading: boolean
  needsReauth: boolean
  handleConnect: () => void
  handleSync: (reset?: boolean) => Promise<void>
  openPicker: () => void
}

export const useGoogleDriveIntegration = (): UseGoogleDriveIntegrationReturn => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  const { getIntegration } = useExternalIntegrations()
  const integration = getIntegration(ExternalIntegrationEnum.GoogleDrive)

  const { initiateOauth } = useInitiateOauth()
  const { openPicker: openPickerFn, isLoading: isPickerLoading } = useGoogleDrivePicker()

  const { data: syncState } = useQuery<Response<GoogleDriveSyncState>, unknown, GoogleDriveSyncState | undefined>(
    ['googleDriveSync', integration?.id],
    () => {
      if (!workspaceId || !integration) {
        throw new Error('Missing required parameters')
      }
      return GoogleDriveClient.show({
        workspaceId,
        integrationId: integration.id,
      })
    },
    {
      enabled: !!integration && !!workspaceId,
      select: (response) => (response.type === 'success' ? response.data : undefined),
      refetchInterval: (data) => (data?.status === 'syncing' ? 2000 : false),
    },
  )

  const isSyncing = syncState?.status === 'syncing'
  const prevIsSyncingRef = useRef(false)

  useEffect(() => {
    const syncJustCompleted = prevIsSyncingRef.current && !isSyncing
    prevIsSyncingRef.current = isSyncing ?? false

    if (syncJustCompleted && workspaceId) {
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])
    }
  }, [isSyncing, workspaceId, queryClient])

  const filesCount = syncState?.watched_file_ids?.length ?? 0
  const needsReauth = syncState?.needs_reauth ?? false

  const lastSyncedAt = syncState?.sync_completed_at
  const lastSync = lastSyncedAt ? dayjs(lastSyncedAt).fromNow() : null

  const handleConnect = () => {
    initiateOauth(ExternalIntegrationEnum.GoogleDrive)
  }

  const handleSync = async (reset = false) => {
    if (!workspaceId || !integration) return

    await GoogleDriveClient.sync({
      workspaceId,
      integrationId: integration.id,
      reset,
    })

    queryClient.invalidateQueries(['googleDriveSync', integration.id])
  }

  const handleSelect = useCallback(
    async (selection: PickerSelection) => {
      if (!workspaceId || !integration) return
      if (selection.files.length === 0) return

      await GoogleDriveClient.update({
        workspaceId,
        integrationId: integration.id,
        watchedFiles: selection.files.map((f) => f.id),
      })
      queryClient.invalidateQueries(['googleDriveSync', integration.id])
    },
    [workspaceId, integration, queryClient],
  )

  const openPicker = useCallback(() => openPickerFn(handleSelect), [openPickerFn, handleSelect])

  return {
    integration,
    isConnected: Boolean(integration),
    filesCount,
    lastSync,
    syncState,
    isSyncing,
    isPickerLoading,
    needsReauth,
    handleConnect,
    handleSync,
    openPicker,
  }
}
